'use client';

// Entrance animation hook for viewport-aware framer-motion animations.
// Disables animations on mobile (≤992px) by returning static state.
// Use `state` + `getKey` for variant-based elements, `useDirectAnimation` for value-based ones (opacity, blur, scale).
// Override breakpoint via options. Default variants are exported separately and can be replaced per use case.

import { useCallback, useMemo, useLayoutEffect, useEffect } from 'react';
import { Variants, useAnimation } from 'framer-motion';
import { useIsMobile } from './useIsMobile';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

export const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export const richtextContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

export const richtextItemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
  },
};

interface Options {
  breakpoint?: number;
}

export function useEntranceAnimation({ breakpoint = 992 }: Options = {}) {
  const isMobile = useIsMobile({ breakpoint });
  const isReady = isMobile !== null;
  const shouldAnimate = isMobile === false;

  const getKey = useCallback(
    (name: string) => `${name}-${isReady ? 'ready' : 'pending'}`,
    [isReady],
  );

  // For variant-based elements: <motion.div key={getKey('x')} {...state} variants={containerVariants}>
  const state = useMemo(
    () =>
      shouldAnimate
        ? { initial: 'hidden' as const, animate: 'visible' as const }
        : { initial: 'visible' as const, animate: 'visible' as const },
    [shouldAnimate],
  );

  // For direct-value elements: <motion.div key={getKey('x')} {...direct({opacity:0},{opacity:1})}>
  // Returns initial=visible on SSR/mobile (shouldAnimate=false) so elements are visible in server HTML.
  // On desktop, key change via getKey triggers remount with initial=hidden → animation plays.
  const direct = useCallback(
    <T extends object>(hidden: T, visible: T) => ({
      initial: shouldAnimate ? hidden : visible,
      animate: visible,
    }),
    [shouldAnimate],
  );

  return { shouldAnimate, isReady, getKey, state, direct };
}

// Use this instead of `direct` + `key` for value-based elements (opacity, scale, etc.).
// Server renders visible (no layout shift), useLayoutEffect hides before first browser paint on desktop,
// then animates in. Pass `transition` as a prop on the motion element itself.
export function useDirectAnimation<T extends object>(hidden: T, visible: T) {
  const { shouldAnimate } = useEntranceAnimation();
  const controls = useAnimation();

  useIsomorphicLayoutEffect(() => {
    if (shouldAnimate) {
      controls.set(hidden);
      controls.start(visible as Parameters<typeof controls.start>[0]);
    } else {
      controls.set(visible);
    }
  }, [shouldAnimate]);

  return { initial: visible, animate: controls };
}
